import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectItem } from '@/types/ui';
import { createStableObjectHash } from '@/storybook/components/FormCollection/utils/utils';

const getValueKey = (valueToCheck: unknown) =>
  valueToCheck !== null && typeof valueToCheck === 'object'
    ? createStableObjectHash(valueToCheck)
    : String(valueToCheck);

type UseMultiLevelSelectOptionsProps<T> = {
  options: SelectItem<T>[];
  unfilteredOptions?: SelectItem<T>[];
  value: SelectItem<T>[];
  memoizedValueList: SelectItem<T>[];
  searchInput?: string;
};

type KeyedSelectItem<T> = SelectItem<T> & { __internalKey: string };

export const useMultiLevelSelectOptionsFloatingLabel = <T>({
  options,
  unfilteredOptions,
  value,
  memoizedValueList,
  searchInput,
}: UseMultiLevelSelectOptionsProps<T>) => {
  const { t } = useTranslation();

  const allOptions = useMemo<KeyedSelectItem<T>[]>(() => {
    const sourceOptions = options || unfilteredOptions || [];
    const noCategoryLabel = t('components.dropdownPanel.noCategory').toLowerCase();

    const validOptions = sourceOptions
      .filter(optionItem => optionItem.value != null && optionItem.label != null)
      .map(optionItem => ({
        ...optionItem,
        __internalKey: getValueKey(optionItem.value),
      }));

    return validOptions.sort((optionA, optionB) => {
      const categoryA = optionA.category?.toLowerCase() ?? noCategoryLabel;
      const categoryB = optionB.category?.toLowerCase() ?? noCategoryLabel;
      return categoryA.localeCompare(categoryB);
    });
  }, [options, unfilteredOptions, t]);

  const selectedKeysSet = useMemo(
    () => new Set(memoizedValueList.map(valueItem => getValueKey(valueItem.value))),
    [memoizedValueList],
  );

  const isSelected = useCallback(
    (optionToCheck: SelectItem<T>) => selectedKeysSet.has(getValueKey(optionToCheck.value)),
    [selectedKeysSet],
  );

  const searchResults = useMemo<KeyedSelectItem<T>[]>(() => {
    if (!searchInput) return allOptions;
    const lowerCaseSearchInput = searchInput.toLowerCase();

    return allOptions.filter(
      optionItem =>
        String(optionItem.label).toLowerCase().includes(lowerCaseSearchInput) ||
        String(optionItem.category || '')
          .toLowerCase()
          .includes(lowerCaseSearchInput),
    );
  }, [allOptions, searchInput]);

  const { selectedOptions, notSelectedOptions } = useMemo(() => {
    const seenSelectedKeys = new Set<string>();
    const seenNotSelectedKeys = new Set<string>();
    const memoizedSelectedKeysSet = new Set(memoizedValueList.map(valueItem => getValueKey(valueItem.value)));

    return searchResults.reduce<{
      selectedOptions: SelectItem<T>[];
      notSelectedOptions: SelectItem<T>[];
    }>(
      (accumulator, optionItem) => {
        const deduplicationKey = optionItem.category
          ? `${optionItem.__internalKey}-${optionItem.category}`
          : optionItem.__internalKey;

        const isOptionSelected = memoizedSelectedKeysSet.has(optionItem.__internalKey);
        const targetArray = isOptionSelected ? 'selectedOptions' : 'notSelectedOptions';
        const seenKeys = isOptionSelected ? seenSelectedKeys : seenNotSelectedKeys;

        const isDuplicate = seenKeys.has(deduplicationKey);
        if (!isDuplicate) {
          seenKeys.add(deduplicationKey);
          accumulator[targetArray].push(optionItem);
        }

        return accumulator;
      },
      { selectedOptions: [], notSelectedOptions: [] },
    );
  }, [searchResults, memoizedValueList]);

  const categoriesToDisplay = useMemo(() => {
    const seenCategories = new Set<string>();

    return notSelectedOptions.reduce<{ category: string; index: number }[]>((accumulator, optionItem, optionIndex) => {
      const categoryName = optionItem.category || 'undefined';
      const isNewCategory = !seenCategories.has(categoryName);

      if (isNewCategory) {
        seenCategories.add(categoryName);
        accumulator.push({ category: categoryName, index: optionIndex });
      }

      return accumulator;
    }, []);
  }, [notSelectedOptions]);

  return {
    allOptions,
    isSelected,
    searchResults,
    selectedOptions,
    notSelectedOptions,
    categoriesToDisplay,
  };
};
